import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { ICTProductDataCurrent } from "@Types/product";
import { getItem, setItem } from "@Utils/browserStorage";
import {
  sendTrackKlaviyoEvent,
  sendViewedItemKlaviyoEvent,
  getFormattedProductForKlaviyo,
  getFormattedCheckoutDataForKlaviyo,
} from "@Utils/klaviyo";
import { getFullPath } from "@Utils/url";
import { ICartState } from "@wff/store/cartSlice";
import { useAppSelector } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";

const STORAGE_TYPE = "session";
const STORAGE_KEY = "activeOnSite";

export const useTrackActiveOnSite = () => {
  useEffect(() => {
    const userWasActive = getItem(STORAGE_KEY, STORAGE_TYPE);

    if (!userWasActive) {
      setItem(STORAGE_KEY, "true", STORAGE_TYPE);
      sendTrackKlaviyoEvent(["Active on Site"]);
    }
  }, []);
};

export const useTrackViewedProduct = (
  currentProduct: ICTProductDataCurrent
) => {
  const currentLocale = useAppSelector(localeSelector)?.languageCode;
  const mountRef = useRef(false);
  const { asPath } = useRouter();
  const url = getFullPath(asPath);

  const formattedProductData = getFormattedProductForKlaviyo(
    currentProduct,
    currentLocale,
    url
  );

  useEffect(() => {
    if (mountRef.current === false) {
      sendViewedItemKlaviyoEvent(formattedProductData);
      sendTrackKlaviyoEvent(["Viewed Product", formattedProductData]);

      mountRef.current = true;
    }
  }, [formattedProductData]);
};

export const useStartedCheckoutProduct = (cartData: ICartState) => {
  const currentLocale = useAppSelector(localeSelector)?.languageCode;
  const { asPath } = useRouter();
  const mountRef = useRef(false);
  const url = getFullPath(asPath);

  const formattedCartData = getFormattedCheckoutDataForKlaviyo(
    cartData,
    currentLocale,
    url
  );

  useEffect(() => {
    if (mountRef.current === false) {
      sendViewedItemKlaviyoEvent(formattedCartData);
      sendTrackKlaviyoEvent(["Started Checkout", formattedCartData]);

      mountRef.current = true;
    }
  }, [formattedCartData]);
};
